import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Box,
  Flex,
  Heading,
  Input,
  Spinner,
  Stack,
  Center,
  Alert,
  AlertIcon,
  Text,
  Button,
  HStack,
  useToast,
  CloseButton,
} from "@chakra-ui/react";
import React, { useContext, useEffect, useMemo, useState } from "react";
import HybridBottomSheetDrawer from "../../components/HybridBottomSheetDrawer";
import { useNavigate } from "react-router-dom";
import { MapContext } from "../../context/map.context";
import { MapPin } from "../../components/Map";
import LocationButton from "../../components/LocationButton";
import { getAllEvents } from "../../data/welcomeEvents.api";
import { WelcomeWeekEvent } from "../../types/welcomeWeekTypes";
import WelcomeCard from "../../components/welcomeProgramme/WelcomeCard";
import { AiOutlineLeftCircle } from "react-icons/ai";

const WelcomeEvents = () => {
  const { setPins } = useContext(MapContext);
  const [events, setEvents] = useState<WelcomeWeekEvent[]>([]);
  const [mapEvents, setMapEvents] = useState<WelcomeWeekEvent[]>([]);
  const [eventPins, setEventPins] = useState<MapPin[]>([]);
  const [loading, setLoading] = useState<boolean>(true); // Loading state
  const [error, setError] = useState<string | null>(null); // Error state
  const [search, setSearch] = useState<string>("");
  const [tooBig, setTooBig] = useState<boolean>(false); // Determines if event count exceeds 150
  const [toastShown, setToastShown] = useState<boolean>(false); // Track whether toast was shown
  const toast = useToast();

  const navigate = useNavigate();

  // Fetch events data on load
  useEffect(() => {
    async function fetchEvents() {
      setLoading(true); // Set loading to true before starting data fetching
      setError(null); // Reset error before fetching
      try {
        const eventsData = await getAllEvents();
        setEvents(eventsData.allData); // All events including ones with no location
        setMapEvents(eventsData.mapData); // Events with a location
      } catch (error) {
        setError("Failed to load events data");
      } finally {
        setLoading(false); // Stop loading after fetch attempt
      }
    }
    fetchEvents();
  }, []);

  // Transforms the event with location into data for Map Pins
  useEffect(() => {
    const pins = [...mapEvents].map((event) => ({
      lng: event.location[0].longitude as number,
      lat: event.location[0].latitude as number,
      onClick: () =>
        navigate(
          `/welcomeprogramme/${event.location[0].latitude}_${event.location[0].longitude}`
        ),
    }));
    setEventPins(pins);
  }, [mapEvents, navigate]);

  // Maps the list of event map pins to visible points on the map
  useEffect(() => {
    setPins([...eventPins]);
  }, [eventPins, setPins]);

  // Filter events with no location by excluding events present in mapEvents
  const eventsWithNoLocation = events.filter(
    (event) => !mapEvents.some((mapEvent) => mapEvent.id === event.id)
  );

  // Retrieve the number of events
  const numberOfEvents = () => {
    const localEventsString = localStorage.getItem("filteredEvents");
    const storedEventsFull = localEventsString
      ? JSON.parse(localEventsString)
      : [];
    return storedEventsFull.length;
  };

  // Check if the number of events exceeds 150
  useEffect(() => {
    if (events.length > 0) {
      const numEvents = numberOfEvents();
      if (numEvents > 150) {
        setTooBig(true);
      }
    }
  }, [events.length]);

  // Show the toast only once when the number of events is more than 150
  useEffect(() => {
    // Check if the toast has been shown before by looking in sessionStorage
    const hasToastBeenShown = sessionStorage.getItem("toastShown");

    if (tooBig && !toastShown && hasToastBeenShown === "false") {
      toast({
        title: "Search very large",
        position: "top",
        description: `Only showing ${
          events.length
        } of the ${numberOfEvents()} events from your search`,
        status: "warning",
        duration: 5000,
        isClosable: true,
      });

      setToastShown(true); // Update local state to prevent immediate re-showing

      sessionStorage.setItem("toastShown", "true");
    }
  }, [tooBig, toastShown, events.length, toast]);

  const filteredMapEvents = useMemo(() => {
    return mapEvents.filter((event) =>
      event.title.toLowerCase().includes(search.toLowerCase())
    );
  }, [mapEvents, search]);

  const filteredEventsWithNoLocation = useMemo(() => {
    return eventsWithNoLocation.filter((event) =>
      event.title.toLowerCase().includes(search.toLowerCase())
    );
  }, [eventsWithNoLocation, search]);

  return (
    <>
      <HybridBottomSheetDrawer ml={"4em"}>
        <CloseButton
          zIndex={999}
          onClick={() => {
            sessionStorage.setItem("toastShown", "false");
            const currentEnv = process.env.NODE_ENV;
            if (currentEnv === "production") {
              window.location.assign(
                "https://www.lancaster.ac.uk/itpi/web/services/welcomeprogramme"
              );
            } else {
              window.location.assign(
                "https://www.lancaster.ac.uk/itpi/test/web/services/welcomeprogramme"
              );
            }
          }}
          position="absolute"
          top={2}
          right={0.5}
        />

        <Box p={8}>
          <Flex mb={4} justify="space-between" align="center">
            <Heading size="md">Welcome Events</Heading>
            <LocationButton variant="outline">Nearby</LocationButton>
          </Flex>

          <Input
            variant="filled"
            placeholder="Search"
            py={2}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />

          <HStack py={2} justifyContent="space-between">
            <Text>{`Showing ${
              events.length
            } of ${numberOfEvents()} events`}</Text>
            <Button
              onClick={() => {
                sessionStorage.setItem("toastShown", "false");
                const currentEnv = process.env.NODE_ENV;
                if (currentEnv === "production") {
                  window.location.assign(
                    "https://www.lancaster.ac.uk/itpi/web/services/welcomeprogramme"
                  );
                } else {
                  window.location.assign(
                    "https://www.lancaster.ac.uk/itpi/test/web/services/welcomeprogramme"
                  );
                }
              }}
              color={"black"}
              size="sm"
              my={1}
            >
              Filters
            </Button>
          </HStack>

          {loading ? (
            <Center>
              <Spinner />
            </Center>
          ) : error ? (
            <>
              <Alert status="error">
                <AlertIcon />
                {error}
              </Alert>
              <Center>
                <Button
                  _hover={{
                    bg: "#fed7d7",
                    color: "black",
                    fontWeight: "normal",
                  }}
                  textAlign={"left"}
                  bg={"#e53e3e"}
                  color={"white"}
                  onClick={() => {
                    sessionStorage.setItem("toastShown", "false");
                    const currentEnv = process.env.NODE_ENV;
                    if (currentEnv === "production") {
                      window.location.assign(
                        "https://www.lancaster.ac.uk/itpi/web/services/welcomeprogramme"
                      );
                    } else {
                      window.location.assign(
                        "https://www.lancaster.ac.uk/itpi/test/web/services/welcomeprogramme"
                      );
                    }
                  }}
                  my={2}
                >
                  <AiOutlineLeftCircle size={20} />
                  <Text ml={2}>{`Return to Welcome Programme`}</Text>
                </Button>
              </Center>
            </>
          ) : events.length > 0 ? (
            <Accordion defaultIndex={[1]} allowMultiple>
              <Text></Text>
              {/* Events with No Location */}
              <AccordionItem>
                <h2>
                  <AccordionButton>
                    <Text as="span" flex="1" textAlign="left">
                      {`Events with No Location (${filteredEventsWithNoLocation.length})`}
                    </Text>
                    <AccordionIcon />
                  </AccordionButton>
                </h2>
                <AccordionPanel pb={4}>
                  <Stack spacing={4}>
                    {filteredEventsWithNoLocation.length > 0 ? (
                      filteredEventsWithNoLocation.map((event) => (
                        <WelcomeCard key={event.id} event={event} />
                      ))
                    ) : (
                      <Text>No events without a location</Text>
                    )}
                  </Stack>
                </AccordionPanel>
              </AccordionItem>

              {/* Events on Map */}
              <AccordionItem>
                <h2>
                  <AccordionButton>
                    <Text as="span" flex="1" textAlign="left">
                      {`Events on Map (${filteredMapEvents.length})`}
                    </Text>
                    <AccordionIcon />
                  </AccordionButton>
                </h2>
                <AccordionPanel pb={4}>
                  <Stack spacing={4}>
                    {filteredMapEvents.length > 0 ? (
                      filteredMapEvents.map((event) => (
                        <WelcomeCard key={event.id} event={event} />
                      ))
                    ) : (
                      <Text>No events with location on the map</Text>
                    )}
                  </Stack>
                </AccordionPanel>
              </AccordionItem>
            </Accordion>
          ) : (
            <Text>No events found</Text>
          )}
        </Box>
      </HybridBottomSheetDrawer>
    </>
  );
};

export default WelcomeEvents;
